import React, { ChangeEvent, useMemo, useState } from "react";
import { Box, FormControl, FormLabel, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface PasswordValidator {
  isHidden?: boolean;
  exampleText: string;
  labelText: string;
  tooltip: React.ReactNode | string;
  test: (newValue: string, confirmValue: string) => boolean;
}

interface PasswordConfirmationProps {
  /**
   * Callback fired when the password values are changed.
   */
  onPasswordChange(isValid: boolean, password: string, confirm: string): void;
  /**
   * Callback fired when the password visibility values are changed.
   */
  onVisibilityChange?(showPassword: boolean, showPasswordConfirm: boolean): void;
  /**
   * The password value.
   */
  password?: string;
  /**
   * The password confirmation value.
   */
  passwordConfirm?: string;
  /**
   * An override value for the password field label.
   * @default "New Password"
   */
  passwordLabel?: string;
  /**
   * If true, the password input text is shown.
   */
  showPassword?: boolean;
  /**
   * If true, the password confirmation text is shown.
   */
  showPasswordConfirm?: boolean;
}

export default function PasswordConfirmation(props: PasswordConfirmationProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [statePassword, setStatePassword] = useState("");
  const [statePasswordConfirm, setStatePasswordConfirm] = useState("");
  const [stateShowPassword, setStateShowPassword] = useState(false);
  const [stateShowPasswordConfirm, setStateShowPasswordConfirm] = useState(false);

  // Abstract the controlled vs uncontrolled logic from values to make the rest of the component simpler.
  const password = props.password === undefined ? statePassword : props.password;
  const passwordConfirm = props.passwordConfirm === undefined ? statePasswordConfirm : props.passwordConfirm;
  const showPassword = props.showPassword === undefined ? stateShowPassword : props.showPassword;
  const showPasswordConfirm = props.showPasswordConfirm === undefined ? stateShowPasswordConfirm : props.showPasswordConfirm;

  const passwordValidators = useMemo<Array<PasswordValidator>>(() => {
    return [
      // Must contain at least 8 characters
      {
        exampleText: i18n.t("change-password.validation.character-count-example"),
        labelText: i18n.t("change-password.validation.character-count-label"),
        tooltip: i18n.t("change-password.validation.character-count-tooltip"),
        test: (newValue: string, confirmValue: string) => newValue.length >= 8
      },
      // Must contain at least one uppercase character
      {
        exampleText: i18n.t("change-password.validation.uppercase-example"),
        labelText: i18n.t("change-password.validation.uppercase-label"),
        tooltip: i18n.t("change-password.validation.uppercase-tooltip"),
        test: (newValue: string, confirmValue: string) => {
          if (newValue.length === 0) 
            return false;
          // Allow for any unicode uppercase character
          // See https://javascript.info/regexp-unicode for more info
          return !!newValue.match(/\p{Lu}/gu);
        }
      },
      // Must contain at least one lowercase character
      {
        exampleText: i18n.t("change-password.validation.lowercase-example"),
        labelText: i18n.t("change-password.validation.lowercase-label"),
        tooltip: i18n.t("change-password.validation.lowercase-tooltip"),
        test: (newValue: string, confirmValue: string) => {
          if (newValue.length === 0) 
            return false;
            // Allow for any unicode lowercase character
            // See https://javascript.info/regexp-unicode for more info
            return !!newValue.match(/\p{Ll}/gu);
        }
      },
      // Must contain at least one number
      {
        exampleText: i18n.t("change-password.validation.number-example"),
        labelText: i18n.t("change-password.validation.number-label"),
        tooltip: i18n.t("change-password.validation.number-tooltip"),
        test: (newValue: string, confirmValue: string) => /[0-9]/.test(newValue)
      },
      // Must contain at least one symbol
      {
        exampleText: i18n.t("change-password.validation.symbol-example"),
        labelText: i18n.t("change-password.validation.symbol-label"),
        tooltip: (
          <>
            <Typography>{i18n.t("change-password.validation.symbol-tooltip-line1")}</Typography>
            <Typography>{i18n.t("change-password.validation.symbol-tooltip-line2")}</Typography>
          </>
        ),
        test: (newValue: string, confirmValue: string) => {
          const symbols = "~!@#\\$%\\^&\\*\\-\\_=\\+\\.\\?";
          return RegExp(`[${symbols}]`).test(newValue);
        }
      },
      // New Password and Confirm Password must match
      {
        exampleText: i18n.t("change-password.validation.match-example"),
        labelText: i18n.t("change-password.validation.match-label"),
        tooltip: `${i18n.t("change-password.validation.match-tooltip")}`,
        test: (newValue: string, confirmValue: string) => newValue.length > 0 && newValue === confirmValue
      }
    ];
  }, [i18n]);

  /**
   * Check whether or not the current state of the component is valid.  If any validator fails, the result will be false.
   * @param newPassword An override for the password state value.  If password is changing this frame, it will need to be overridden here.
   * @param newPasswordConfirm An override for the passwordConfirm state value.  If passwordConfirm is changing this frame, it will need to be overridden here.
   * @returns true if the component is valid, otherwise false.
   */
  function getIsValid(newPassword: string | null, newPasswordConfirm?: string | null): boolean {
    // Check to see if any validators are returning false.  If so, we know the values are invalid.
    const found = passwordValidators.find((validator) => {
      return !validator.test(newPassword || password, newPasswordConfirm || passwordConfirm);
    });
    // The password is valid if we found no invalid validators.
    return !found;
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>): void {
    // All validators need to be run, but with updated state.  To do this we need to pass in 
    // override values because the React state will not be updated in the current frame.
    const isValid = getIsValid(event.target.value);

    if (props.password === undefined) {
      // This is an uncontrolled component that needs to handle its own state.
      setStatePassword(event.target.value);
    }
    // Dispatch a change event for a parent components to handle.
    props.onPasswordChange(isValid, event.target.value, passwordConfirm);
  }

  function handlePasswordConfirmChange(event: ChangeEvent<HTMLInputElement>): void {
    // All validators need to be run, but with updated state.  To do this we need to pass in 
    // override values because the React state will not be updated in the current frame.
    const isValid = getIsValid(null, event.target.value);

    if (props.passwordConfirm === undefined) {
      // This is an uncontrolled component that needs to handle its own state.
      setStatePasswordConfirm(event.target.value);
    }
    // Dispatch a change event for a parent components to handle.
    props.onPasswordChange(isValid, password, event.target.value);
  }

  function handleShowPasswordChange(): void {
    if (props.onVisibilityChange === undefined) {
      // This is an uncontrolled component that needs to handle its own state.
      setStateShowPassword(!showPassword);
    } else {
      // This is a controlled component.  Dispatch a change event for a parent component to handle.
      props.onVisibilityChange(!showPassword, showPasswordConfirm);
    }
  }

  function handleShowPasswordConfirmChange(): void {
    if (props.onVisibilityChange === undefined) {
      // This is an uncontrolled component that needs to handle its own state.
      setStateShowPasswordConfirm(!showPasswordConfirm);
    } else {
      // This is a controlled component.  Dispatch a change event for a parent component to handle.
      props.onVisibilityChange(showPassword, !showPasswordConfirm);
    }
  }

  // Run each validator to check the password state and build the UI elements for the filter.
  const validationElements = passwordValidators.reduce<Array<React.JSX.Element>>((memo, validator) => {
    const isValid = validator.test(password, passwordConfirm);
    
    if (validator.isHidden) return memo;
    
    const color = isValid ? theme.colors.namedColors.positive : "";
    let element = (
      <Box
        key={validator.labelText}
        sx={{
          flex: 1,
          color: color
        }}>
        <Box
          sx={{
            fontSize: "1.5em",
            textAlign: "center"
          }}>{validator.exampleText}</Box>
        <Box sx={{
          textAlign: "center"
        }}>{validator.labelText}</Box>
      </Box>
    );
    if (validator.tooltip) {
      element = (
        <Tooltip key={validator.labelText+"tt"} title={validator.tooltip}>{element}</Tooltip>
      )
    }
    memo.push(element);
    return memo;
  }, []);

  return (
    <Stack spacing={1}>
      <Box>{i18n.t('change-password.requirements')}</Box>
      <Stack
        direction="row"
        sx={{
          paddingTop: 2,
          paddingBottom: 2,
          justifyContent: "space-between"
        }}>
        {validationElements}
      </Stack>
      <FormControl>
        <FormLabel sx={{fontSize: "1rem"}}>{props.passwordLabel || i18n.t('change-password.new-password-label')}</FormLabel>
        <Stack direction="row" spacing={1}>
          <TextField fullWidth autoComplete="off" type={showPassword ? "text" : "password"} label="" size="small" value={password} onChange={handlePasswordChange} />
          <IconButton onClick={handleShowPasswordChange}>
            {showPassword && <VisibilityIcon />}
            {!showPassword && <VisibilityOffIcon />}
          </IconButton>
        </Stack>
      </FormControl>
      <FormControl>
        <FormLabel sx={{fontSize: "1rem"}}>{i18n.t('change-password.confirm-password-label')}</FormLabel>
        <Stack direction="row" spacing={1}>
          <TextField fullWidth autoComplete="off" type={showPasswordConfirm ? "text" : "password"} label="" size="small" value={passwordConfirm} onChange={handlePasswordConfirmChange}/>
          <IconButton onClick={handleShowPasswordConfirmChange}>
            {showPasswordConfirm && <VisibilityIcon />}
            {!showPasswordConfirm && <VisibilityOffIcon />}
          </IconButton>
        </Stack>
      </FormControl>
    </Stack>
  );
}